// Schermata «Oggi» (tela D9): i numeri del giorno, i prossimi trattamenti, le cose da sapere.
// Con il ponte acceso, in «Da sapere» anche le richieste di RVC da rispondere. Partenze e magazzino arrivano dopo.
import { doc, onSnapshot } from 'firebase/firestore';
import { t } from '../i18n.js';
import { h, sostituisci, app, chiudiFoglio } from './dom.js';
import { oggiRoma, giornoLungo } from './date.js';
import { ascoltaGiornoIntero, creaPrenotazioni } from '../dati/prenotazioni.js';
import {
  OPERATRICI, toMin, hm, fine, occupa, eur, incasso, salaNome, adessoRoma, blocchiDel, noShowDaDecidere,
} from '../logica/agenda.js';
import { occupazione, prossimi } from '../logica/oggi.js';
import { TRATTAMENTI } from '../logica/listino.js';
import { admin as eAdmin } from '../logica/ruoli.js';
import { apriScheda, testoStato } from './prenotazione.js';
import { testata, pannelloRichieste } from './schermate.js';

/**
 * @param {{ vaiAgenda: (giorno?: string) => void, richieste?: object, vaiRichieste?: () => void }} sezioni per passare
 *   all'agenda (anche dopo una modifica) · richieste = elenco delle richieste di RVC (solo con il ponte acceso)
 */
export function schermataOggi(ctx, dispositivo, { vaiAgenda, richieste: fonteRichieste = null, vaiRichieste }) {
  const ruoli = dispositivo.ruoli || [];
  const admin = eAdmin(ruoli);
  const scritture = creaPrenotazioni({ ...ctx, nome: dispositivo.nome || '' });
  const giorno = oggiRoma();
  let ultimo = null;
  let nomi = {};
  let daRispondere = 0;
  const stato = h('p', { classe: 'stato-rete', 'aria-live': 'polite' });
  const richieste = h('div');
  const numeri = h('div', { classe: 'numeri', id: 'numeri' });
  const lista = h('ul', { classe: 'lista', id: 'prossimi' });
  const sapere = h('ul', { classe: 'lista', id: 'da-sapere' });

  const vista = {
    ctx, dispositivo, ruoli, scritture,
    data: () => giorno,
    dati: () => ultimo,
    nomi: () => nomi,
    vaiA: (k) => vaiAgenda(k),
  };
  const breve = (o) => nomi[o] || t('op.breve', { n: OPERATRICI.indexOf(o) + 1 });
  const vuota = (testo) => h('li', { classe: 'vuota' }, testo);

  function riga({ b, partner }) {
    const tr = TRATTAMENTI[b.tr]?.nome || b.tr;
    const dove = partner ? t('oggi.lettini', { sala: salaNome(b.sala) }) : t('oggi.lettino', { sala: salaNome(b.sala), n: b.lettino });
    const chi = partner ? t('oggi.due', { a: breve(b.op), b: breve(partner.op) }) : breve(b.op);
    return h('li', {}, h('button', { type: 'button', classe: 'riga', dati: { id: b.id }, onclick: () => apriScheda(vista, b.id) },
      h('span', { classe: 'ora' }, hm(toMin(b.inizio)), h('span', {}, hm(fine(b)))),
      h('span', { classe: 'cresce' },
        h('span', { classe: 'nome' }, partner ? t('oggi.coppia', { tr }) : tr),
        h('span', { classe: 'piccolo' }, [b.camera, b.nome || t('agenda.ospite'), dove, chi].filter(Boolean).join(' · '))),
      h('span', { classe: `chip st-${b.stato}` }, testoStato(b))));
  }

  function disegna() {
    if (!ultimo) return;
    const pr = ultimo.prenotazioni;
    const n = pr.filter(occupa).length;
    sostituisci(numeri,
      h('div', {}, h('b', {}, String(n)), h('small', {}, n === 1 ? t('oggi.tratt1') : t('oggi.trattN'))),
      h('div', {}, h('b', {}, `${occupazione(pr)}%`), h('small', {}, t('oggi.occupati'))),
      h('div', {}, h('b', {}, eur(pr.reduce((x, b) => x + incasso(b), 0))), h('small', {}, t('oggi.incasso'))));
    const voci = prossimi(pr, adessoRoma().minuti);
    sostituisci(lista, voci.length ? voci.map(riga) : vuota(t('oggi.nessuno')));

    // Da sapere: fuori servizio di oggi e (solo responsabile) no-show con l'importo da decidere.
    const fuori = blocchiDel(ultimo, giorno, null).map((x) => h('li', { classe: 'riga' },
      h('span', { classe: 'ora' }, hm(toMin(x.inizio)), h('span', {}, hm(toMin(x.fine)))),
      h('span', { classe: 'cresce' },
        h('span', { classe: 'nome' }, `${salaNome(x.sala)} · ${+x.lettino ? t('ctrl.lettinoN', { n: x.lettino }) : t('ctrl.tuttiLettini')} ${t('agenda.fuoriServizio')}`),
        x.motivo ? h('span', { classe: 'piccolo' }, x.motivo) : null)));
    const daDecidere = admin ? pr.filter(noShowDaDecidere) : [];
    const noShow = daDecidere.length ? h('li', {}, h('button', {
      type: 'button', classe: 'riga', id: 'oggi-noshow', onclick: () => apriScheda(vista, daDecidere[0].id),
    }, h('span', { classe: 'cresce' }, h('span', { classe: 'nome' }, t('agenda.noShowDecidere', { n: daDecidere.length }))))) : null;
    const daRvc = daRispondere ? h('li', {}, h('button', {
      type: 'button', classe: 'riga', id: 'oggi-richieste', onclick: () => vaiRichieste(),
    }, h('span', { classe: 'cresce' }, h('span', { classe: 'nome' }, t('oggi.richiesteRvc', { n: daRispondere }))))) : null;
    sostituisci(sapere, daRvc || noShow || fuori.length ? [daRvc, noShow, fuori] : vuota(t('oggi.niente')));
  }

  const stopGiorno = ascoltaGiornoIntero(ctx, giorno, (x) => {
    ultimo = x;
    stato.textContent = x.dallaCache && !navigator.onLine ? t('rete.no') : '';
    disegna();
  }, (e) => ctx.scatola.registra(e, { dove: 'oggi' }));
  // Nomi delle operatrici: scritti nell'app dalla responsabile (campo «operatrici» della struttura), mai nel codice.
  const stopStruttura = onSnapshot(doc(ctx.db, `strutture/${ctx.sid}`), (d) => {
    nomi = (d.exists() && d.data().operatrici) || {};
    disegna();
  }, (e) => ctx.scatola.registra(e, { dove: 'struttura' }));

  sostituisci(app(), h('main', { classe: 'pagina oggi', id: 'oggi-pagina' },
    testata(t('oggi.luogo')),
    h('h1', { classe: 'data' }, giornoLungo(giorno)),
    stato, richieste, numeri,
    h('h2', { classe: 'sez' }, t('oggi.prossimi'),
      h('button', { type: 'button', id: 'oggi-agenda', onclick: () => vaiAgenda(giorno) }, t('oggi.vaiAgenda'))),
    lista,
    h('h2', { classe: 'sez' }, t('oggi.daSapere')),
    sapere));
  sostituisci(lista, vuota(t('agenda.inCarico')));
  const stopRichieste = admin ? pannelloRichieste(ctx, richieste) : () => {};
  const stopRvc = fonteRichieste ? fonteRichieste.ascolta((lista) => {
    daRispondere = lista.filter((r) => r.stato === 'da-rispondere').length;
    disegna();
  }) : () => {};
  // I trattamenti finiti escono dai «prossimi» da soli: ridisegno ogni minuto.
  const orologio = setInterval(disegna, 60_000);
  return () => { clearInterval(orologio); chiudiFoglio(); stopGiorno(); stopStruttura(); stopRichieste(); stopRvc(); };
}
